import { errorHandling } from "./errorHandling.js";
import { retry } from "./retry.js";
import { sherbertFetch } from "./sherbertFetch.js";
/**
 * Fetch wrapper around XMLHttpRequest to have same interface as fetch
 * @param input - The input for the fetch
 * @param init - The init for the fetch
 * @returns The response from the fetch
 * @example
 * ```ts
 * const response = await xhrFetch(
 *   "https://api.sherbert.cimpress.io/v2/assets",
 *   { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: { file: new File([], "test.png") }, onProgress: (progress) => { console.log(progress) } },
 * );
 * ```
 */
async function xhrFetch(input, init) {
    if (typeof XMLHttpRequest === "undefined") {
        throw new Error("XMLHttpRequest is not supported in this environment. Don't call this function directly.");
    }
    return new Promise((resolve, reject) => {
        // Some limitations for this function for now
        if (!init.body) {
            return reject(new Error("Body is required"));
        }
        if (init.method !== "POST") {
            return reject(new Error("Method must be POST"));
        }
        if (!(init.body instanceof FormData) && typeof init.body !== "string") {
            return reject(new Error("Body must be FormData or JSON string"));
        }
        const xhr = new XMLHttpRequest();
        xhr.open(init.method, input.toString());
        // Copy headers for request
        if (init.headers) {
            Object.entries(init.headers).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });
        }
        // Setup progress handling
        if (init.onProgress) {
            xhr.upload.addEventListener("progress", (event) => {
                if (event.lengthComputable) {
                    init.onProgress?.({
                        loaded: event.loaded,
                        total: event.total,
                    });
                }
            });
        }
        const handleAbort = () => {
            reject(new Error(init.signal?.reason || "Request aborted"));
        };
        // Return a Response object
        xhr.onload = () => {
            if (init.signal?.aborted) {
                return handleAbort();
            }
            const response = new Response(xhr.response, {
                status: xhr.status,
                statusText: xhr.statusText,
                headers: xhr
                    .getAllResponseHeaders()
                    .split("\r\n")
                    .reduce((acc, header) => {
                    const [key, value] = header.split(": ");
                    if (key)
                        acc.append(key, value);
                    return acc;
                }, new Headers()),
            });
            Object.defineProperty(response, "url", { value: xhr.responseURL });
            resolve(response);
        };
        xhr.onerror = () => reject(new Error("Network request failed"));
        xhr.ontimeout = () => reject(new Error("Network request timed out"));
        // Setup abort listeners
        xhr.onabort = handleAbort;
        // Handle signal abort
        if (init.signal) {
            // If already aborted, reject immediately
            if (init.signal.aborted) {
                handleAbort();
                return;
            }
            init.signal.addEventListener("abort", () => {
                xhr.abort();
                // For some reason, the abort event is not fired. Call reject manually
                handleAbort();
            });
        }
        xhr.send(init.body);
    });
}
/**
 * Fetch wrapper around XMLHttpRequest to handle errors, retries. Does not support long polling
 * Use sherbertFetch to use the fetch function instead of xhrFetch
 * Only supports POST requests at this time
 * Function is useful for uploading files and getting progress updates
 * @param args - The arguments for the fetch
 * @param options - The options for the fetch
 * @param options.onProgress - The function to call with progress updates
 * @returns The response from the fetch
 * @example
 * ```ts
 * const response = await xhrSherbertFetch(() => [
 *   "https://api.sherbert.cimpress.io/v2/assets",
 *   { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: { file: new File([], "test.png") }, onProgress: (progress) => { console.log(progress) } },
 * ]);
 * ```
 */
export async function xhrSherbertFetch(
// args is a function to make sure the arguments are recreated on retry.
// A new auth token might be needed
args, options) {
    if (typeof XMLHttpRequest === "undefined") {
        return sherbertFetch(args);
    }
    const innerFetch = () => {
        const [input, init] = args();
        return xhrFetch(input, { ...init, ...options });
    };
    return errorHandling(() => retry(() => innerFetch()), args);
}
